import { MockStudent } from '../data/mock-students';
import { GameQuestion, QuizSubject } from './question.interface';

export enum MiniGameType {
  CLASSIC_QUIZ = 'classic_quiz',
  SPIN_WHEEL = 'spin_wheel',
  TUG_OF_WAR = 'tug_of_war',
  LIGHTNING_ROUND = 'lightning_round',
  BOMB_PASS = 'bomb_pass',
  MYSTERY_BOX = 'mystery_box',
  BOWLING = 'bowling',
  SYMMETRY = 'symmetry',
  MATCH_UP = 'match_up',
  FRACTION_LINE = 'fraction_line',
  FRACTION_HOPS = 'fraction_hops',
  FRACTION_TREK = 'fraction_trek',
  ZOOM_QUEST = 'zoom_quest',
  TREASURE_GRID = 'treasure_grid',
  CUBE_FILL = 'cube_fill',
  BALANCE_IT = 'balance_it',
  POWER_BURST = 'power_burst',
  CUTTING_PIZZA = 'cutting_pizza',
  VAULT_CRACKER = 'vault_cracker'
}

export interface MiniGameConfig {
  type: MiniGameType;
  name: { en: string; ar: string };
  description: { en: string; ar: string };
  icon: string;
  color: string;
  questionsCount: number;
  timePerQuestion: number;
  requiresStudents: boolean;
  minStudents: number;
  xpMultiplier: number;
}

export interface MiniGameStudent extends MockStudent {
  avatarColor: string;
  initials: string;
  teamId?: 'A' | 'B';
  score?: number;
  isActive?: boolean;
}

export interface StudentTeam {
  id: 'A' | 'B';
  name: { en: string; ar: string };
  color: string;
  students: MiniGameStudent[];
  score: number;
}

export interface MiniGameSession {
  gameType: MiniGameType;
  config: MiniGameConfig;
  students: MiniGameStudent[];
  teams?: { teamA: StudentTeam; teamB: StudentTeam };
  subject: QuizSubject | 'random';
  questions: GameQuestion[];
  currentQuestionIndex: number;
  answers: MiniGameAnswer[];
  totalXP: number;
  startedAt: Date;
  gameState: any;
}

export interface MiniGameAnswer {
  questionId: string;
  selectedOptionId: 'A' | 'B' | 'C' | 'D' | null;
  isCorrect: boolean;
  xpEarned: number;
  studentId?: number;
  teamId?: 'A' | 'B';
}

/**
 * Per-round timing/tries captured generically by the shell base
 * (`ImmersiveGameBase`) and forwarded into `score<Game>Round` → the shared
 * `gameWorld-round` skill-analytics event. See ig-game-theme.md §3
 * (Skill analytics rule).
 */
export interface RoundOutcomeMeta {
  failedTries: number;             // wrong submissions before the outcome
  reactionTimeMs: number | null;   // round shown → first interaction/submission
  timeToCorrectMs: number | null;  // round shown → correct solve (null if skipped/missed)
}

// --- Per-game state types ---

export interface SpinWheelState {
  currentAngle: number;
  targetAngle: number;
  isSpinning: boolean;
  selectedStudentId: number | null;
  studentResults: { studentId: number; correct: number; total: number }[];
}

export interface TugOfWarState {
  ropePosition: number; // -100 to +100, 0 = center
  currentRound: number; // 0-based
  totalRounds: number;
  teamASubmitted: boolean;
  teamBSubmitted: boolean;
  teamACorrect: boolean | null;
  teamBCorrect: boolean | null;
  pullHistory: { team: 'A' | 'B'; correct: boolean }[];
}

export interface LightningRoundState {
  comboCount: number;
  maxCombo: number;
  comboMultiplier: number;
  flashResult: 'correct' | 'incorrect' | null;
  intensity: number; // 0-3
}

export interface BombPassState {
  currentHolderId: number;
  fuseTime: number;
  explosions: { studentId: number; questionIndex: number }[];
  passOrder: number[];
  currentPassIndex: number;
}

export type BoxCategory = 'bonus' | 'bamboozle';

export enum BoxModifier {
  // Bonus cards
  DOUBLE_XP = 'double_xp',
  TRIPLE_XP = 'triple_xp',
  FREE_PASS = 'free_pass',
  EXTRA_TIME = 'extra_time',
  SHIELD = 'shield',
  // Bamboozle cards
  HALF_TIME = 'half_time',
  FREEZE = 'freeze',
  STEAL = 'steal',
  MINUS_SCORE = 'minus_score',
  REVERSE = 'reverse'
}

export const BOX_MODIFIER_CATEGORY: Record<BoxModifier, BoxCategory> = {
  [BoxModifier.DOUBLE_XP]: 'bonus',
  [BoxModifier.TRIPLE_XP]: 'bonus',
  [BoxModifier.FREE_PASS]: 'bonus',
  [BoxModifier.EXTRA_TIME]: 'bonus',
  [BoxModifier.SHIELD]: 'bonus',
  [BoxModifier.HALF_TIME]: 'bamboozle',
  [BoxModifier.FREEZE]: 'bamboozle',
  [BoxModifier.STEAL]: 'bamboozle',
  [BoxModifier.MINUS_SCORE]: 'bamboozle',
  [BoxModifier.REVERSE]: 'bamboozle'
};

export interface MysteryBoxState {
  boxes: { id: number; modifier: BoxModifier; isRevealed: boolean; isUsed: boolean }[];
  currentModifier: BoxModifier | null;
  modifierRevealed: boolean;
}

// --- Leaderboard ---

export interface StudentScoreEntry {
  student: MiniGameStudent;
  xp: number;
  correctCount: number;
  totalAnswered: number;
  rank: number;
  previousRank: number;
  rankChange: 'up' | 'down' | 'same' | 'new';
  lastXpGain: number;
  streak: number;
}
